import { IFormInputMessage } from '@grubhubprod/gh-cookbook-react';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import {
  getPaths,
  hasAnyValue,
  CheckboxValueType,
  extractErrorMessage,
} from 'src/components/CookbookCheckboxWrapper/hooks/useGroupCheckboxValidate.utils';

export type ValidatorFunction = (
  values: Record<string, CheckboxValueType>
) => boolean | string;

export interface UseGroupCheckboxValidateProps {
  name: string;
  validateGroup: ValidatorFunction | ValidatorFunction[];
  optionKeys: string[];
}

export interface UseGroupCheckboxValidateReturn {
  validate: (value: CheckboxValueType) => boolean | string;
  error?: IFormInputMessage;
}

/**
 * This hook provides group-level validation for a collection of checkboxes
 * managed by `react-hook-form`. It allows multiple custom validation functions
 * to be run against the entire checkbox group. The hook triggers validation
 * for all related checkboxes whenever any single one is updated, ensuring
 * consistent validation state across the group.
 */
export const useGroupCheckboxValidate = ({
  name,
  validateGroup,
  optionKeys = [],
}: UseGroupCheckboxValidateProps): UseGroupCheckboxValidateReturn => {
  const { trigger, errors, getValues, clearErrors, formState } =
    useFormContext();
  const isDirty = Boolean(formState.dirtyFields[name]);
  const hasError = Boolean(errors[name]);

  const validators = Array.isArray(validateGroup)
    ? validateGroup
    : [validateGroup];

  const triggerValidationForGroup = useCallback(() => {
    const values = getValues()[name] || {};
    getPaths(name, values).forEach((path) => trigger(path));
  }, [trigger, name, getValues]);

  useEffect(() => {
    const values = getValues()[name] || {};
    if (isDirty || hasError || hasAnyValue(values)) {
      triggerValidationForGroup();
    }
  }, [isDirty, hasError, triggerValidationForGroup, name, getValues]);

  function validate(): boolean | string {
    const values = getValues()[name] || {};

    if (Object.keys(values).length !== optionKeys.length) {
      return true;
    }

    const isValid: boolean | string = validators.reduce(
      (errorMessage, validator) =>
        typeof errorMessage === 'string'
          ? errorMessage
          : validator(values) || errorMessage,
      true
    );

    if (isValid === true) {
      clearErrors(name);
      return true;
    }

    return isValid;
  }

  const error = extractErrorMessage(errors[name]);

  return { validate, error };
};
